/** @format */
import {CartCheckoutContext} from 'frontier/cart/components/cart/cart_checkout_context';
import React, {useState, useEffect, useContext, useRef} from 'react';
import {addCommas} from 'frontier/utilities/add_comas_to_number';
import {SHIPPING_PROVIDER_FREIGHT} from 'frontier-constants';
import {If, IfElse} from 'frontier-utilities';
import {Select} from '@sttark/design-system';
import SuperAgent from 'superagent';

export default function SingleShipmentMethods({
    shippingMethodProvider,
    isSaturdayDelivery,
    shippingMethodCode,
    shipmentId,
    addressId
}) {
    const ctx = useContext(CartCheckoutContext);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [ratesError, setRatesError] = useState(false);
    const [methods, setMethods] = useState([]);

    const shippingMethodError =
        ctx.validationErrors?.shipping?.[shipmentId]?.shipping_method_code || undefined;

    const [selectedShippingMethod, setSelectedShippingMethod] = useState([
        shippingMethodCode,
        shippingMethodProvider,
        isSaturdayDelivery,
        ''
    ]);

    async function handleChooseShippingMethod(value) {
        try {
            setIsUpdating(true);
            const [code, provider = false, isSaturdayDelivery = 'false', shippingMethodName = ''] =
                value.split(',');
            if (!provider) {
                setSelectedShippingMethod([]);
            } else {
                const booleanSaturdayDelivery = isSaturdayDelivery !== 'false' ? true : false;
                setSelectedShippingMethod([
                    code,
                    provider,
                    booleanSaturdayDelivery,
                    shippingMethodName
                ]);
                const method = methods.find(
                    ({code: c, provider: p, metadata: {is_saturday_delivery: isd}}) => {
                        return c === code && p === provider && isd === booleanSaturdayDelivery;
                    }
                );

                const {
                    body: {shipment, cart}
                } = await SuperAgent.post(`/account/shipments/set-shipping-method/${shipmentId}`)
                    .set('accept', 'application/json')
                    .send({...method});

                const ShipmentIndex = ctx.shipments.findIndex(({id}) => id === shipmentId);

                ctx.onUpdateWithoutSave({
                    cart: {
                        shipping: {$set: cart.shipping},
                        tax: {$set: cart.tax},
                        total: {$set: cart.total}
                    },
                    shipments: {
                        [ShipmentIndex]: {
                            shipping_method_code: {
                                $set: shipment.shipping_method_code
                            },
                            shipping_method_provider: {
                                $set: shipment.shipping_method_provider
                            },
                            shipping_method_name: {
                                $set: shipment.shipping_method_name
                            },
                            is_saturday_delivery: {
                                $set: shipment.is_saturday_delivery
                            },
                            quoted_delivery_date: {
                                $set: shipment.quoted_delivery_date
                            }
                        }
                    },
                    validationErrors: {$set: {}}
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsUpdating(false);
        }
    }

    const fetchRequestRef = useRef(false);

    useEffect(() => {
        if (fetchRequestRef.current) {
            fetchRequestRef.current.abort();
        }
        async function fetchShippingMethods() {
            setRatesError(false);
            setIsLoading(true);
            setMethods([]);
            setSelectedShippingMethod([shippingMethodCode, shippingMethodProvider]);
            try {
                fetchRequestRef.current = SuperAgent.get(
                    `/account/shipments/get-rates/${shipmentId}`
                ).set('accept', 'application/json');

                const {body} = await fetchRequestRef.current;

                setMethods(body?.rates);
            } catch (e) {
                setRatesError(e?.response?.body?.message);
            }
            setIsLoading(false);
        }

        fetchShippingMethods();

        return () => {
            if (fetchRequestRef.current) {
                fetchRequestRef.current.abort();
            }
        };
    }, [addressId]);

    // Shipping method is lost once address is changed, if the user has already selected
    // the shipping method they should not have to reselect it again. This will
    // repopulate the shipping method if it was already selected
    useEffect(() => {
        const currentShipment = ctx.shipments.find(({id}) => id === shipmentId);
        if (!currentShipment) return;

        let shippingMethodName =
            currentShipment.shipping_methods[currentShipment.shipping_method_provider];
        shippingMethodName = shippingMethodName
            ? shippingMethodName[
                  `${currentShipment.shipping_method_provider},${currentShipment.shipping_method_code}`
              ]
            : undefined;

        if (!shippingMethodName) return;

        if (currentShipment) {
            if (currentShipment.shipping_method_code) {
                setSelectedShippingMethod([
                    currentShipment.shipping_method_code,
                    currentShipment.shipping_method_provider,
                    currentShipment.is_saturday_delivery,
                    shippingMethodName
                ]);
            }
        }
    }, [addressId]);

    return (
        <div
            className="single-shipment-methods-11kt checkout-input-section-02hg"
            data-error={!!shippingMethodError ? 'shipping' : ''}>
            <If condition={methods?.length > 0}>
                <div className="container_shipping-address">
                    <IfElse condition={shippingMethodCode}>
                        <h6 className="color-accent-primary m-0">
                            <i className="fa fa-check me-2" />
                            2. Shipping method
                        </h6>
                        <span>2. Choose shipping method</span>
                    </IfElse>
                </div>
                <Select
                    rows={5}
                    value={selectedShippingMethod.join(',')}
                    initialValue={selectedShippingMethod[3]}
                    error={shippingMethodError}
                    label={'*Shipping Method'}
                    placeholder={'Select shipping method'}
                    onChange={handleChooseShippingMethod}
                    options={methods?.map(({name, total, code, provider, metadata}, index) => {
                        name =
                            provider === SHIPPING_PROVIDER_FREIGHT
                                ? `${name} - Dock Required`
                                : name;

                        name = metadata.is_saturday_delivery ? `${name} - Saturday Delivery` : name;
                        return {
                            label: name,
                            value: `${code},${provider},${metadata.is_saturday_delivery},${name}`,
                            rightLabel: {label: addCommas(`$${total}`)}
                        };
                    })}
                />
                <If condition={isUpdating}>
                    <div className="color-accent-primary">
                        <i className="fa fa-spinner fa-spin" /> saving...
                    </div>
                </If>
            </If>
            <If condition={ratesError}>
                <div className="alert alert-danger">{ratesError}</div>
            </If>
            <If condition={isLoading}>
                <div>
                    Loading Shipping Methods from UPS and various Freight Carriers...
                    <i className="fa fa-spin fa-spinner ms-3" />
                </div>
            </If>
        </div>
    );
}
